import { t } from 'i18next';
import * as yup from 'yup';

import { rulesEndpoints } from '@/api/endpoints';
import { EMPTY_CONDITION } from '@/components/organisms/ConditionField/constants';
import { isConditionComplete } from '@/components/organisms/ConditionField/utils';
import { Condition } from '@/shared/types/condition';
import { Action, ActionType, Rule, Trigger } from '@/shared/types/rules';
import { LENGTH_M } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { isActionComplete } from '../ActionsField/utils';
import { TRIGGERS_VALUE_TO_LABEL } from '../TriggersField/constants';

export type RuleFormValues = {
  name: string;
  description: string;
  triggers: Trigger[];
  condition: Condition;
  actions: Action[];
};

export const NAME_STEP_FIELDS: (keyof RuleFormValues)[] = ['name', 'description'];

export type RuleNameValues = Pick<RuleFormValues, 'name' | 'description'>;

const createNameSchema = (deskId: string, excludeName?: string) =>
  createNameFieldSchema({
    getEndpoint: (name) => rulesEndpoints.checkName(deskId, name, { desk_id: deskId }),
    excludeName,
  });

const descriptionSchema = yup
  .string()
  .max(LENGTH_M, () => t('validation.less_than', { 0: LENGTH_M }));

export const createRenameRuleSchema = (deskId: string, currentName: string) =>
  yup.object({ name: createNameSchema(deskId, currentName), description: descriptionSchema });

export const createRuleFormSchema = ({ deskId, isCreate }: { deskId: string; isCreate: boolean }) =>
  yup.object({
    name: isCreate ? createNameSchema(deskId) : yup.string(),
    description: descriptionSchema,
    triggers: yup.array().min(1, () => t('validation.required')),
    condition: yup
      .mixed<Condition>()
      .test('complete', () => t('rules.editor.conditions_incomplete'), isConditionComplete),
    actions: yup
      .array<Action>()
      .min(1, () => t('validation.required'))
      .test(
        'complete',
        () => t('rules.editor.actions_incomplete'),
        (actions = []) => actions.every(isActionComplete)
      ),
  });

export const toRuleFormValues = (rule: Rule): RuleFormValues => ({
  name: rule.name,
  description: rule.description ?? '',
  triggers: rule.triggers ?? [],
  condition: rule.condition ?? EMPTY_CONDITION,
  actions: rule.actions ?? [],
});

export const getNewRuleValues = (brain?: {
  brainParentId: string;
  version: number;
}): RuleFormValues => ({
  name: '',
  description: '',
  triggers: [{ type: TRIGGERS_VALUE_TO_LABEL[0] }],
  condition: EMPTY_CONDITION,
  actions: brain
    ? [
        {
          type: ActionType.ASSIGN_BRAIN,
          brain_parent_id: brain.brainParentId,
          brain_version: brain.version,
        },
      ]
    : [],
});
